import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    Inject,
    inject,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { FormsState } from '../../store/state/pace-forms.state';
import { GroupOption } from '../../types/group-option';
import { combineLatest, debounceTime, distinctUntilChanged, filter, map, Observable, take, withLatestFrom } from 'rxjs';
import { ConfigConstants, ConfigService, isDefined, UiResponsiveService } from '@evorait/pace-core';
import {
    updateDropDownOptions,
    updateFunctionalElementRemarks,
    updateOptionSet,
    updateValue,
} from '../../store/actions/functional-elements.actions';
import { FormsConstants } from '../../types/forms-constants';
import {
    getFormFilters,
    getFormFunctionalElementByIdFactory,
    getFormParticipantCopyReadonly,
    getFormSelectedParticipantCopyId,
    getFormTypeFilters,
    isCosimaFunctionalElementFromTrainingChecklistReadOnly,
} from '../../store/selectors/form.selector';
import { DropDownOptionsFunctionalElement, FunctionTypes } from '../../types/drop-down-options';
import { ContentId } from '../../types/content-block';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { FormUtilities } from '../../utilities/form-utilities';
import { FormTypeFilters } from '../../types/form-type-filters';
import { FormFilters } from '../../types/form-filter';
import { ParticipantCopyId } from '../../types/participant-copy';
import { CHOICE_TYPE } from '../../types/choice';
import { isCosimaFunctionalElementVisible } from '../../utilities/answer-utilities';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'pace-forms-drop-down-options-functional-element',
    templateUrl: './drop-down-options-functional-element.component.html',
    styleUrls: ['drop-down-options-functional-element.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropDownOptionsFunctionalElementComponent implements OnInit, AfterViewInit, OnDestroy {
    private readonly configService = inject(ConfigService);
    @Input()
    public id: ContentId;

    @Input()
    public parentForm: FormArray | undefined;

    public dropDownOptionsFunctionalElement$: Observable<DropDownOptionsFunctionalElement>;
    public elementTypeLabel$: Observable<string>;
    public showRemarksField$: Observable<boolean>;
    public readOnly$: Observable<boolean>;
    public visible$: Observable<boolean>;
    public disableDropDown$: Observable<boolean>;
    public typeFilters$: Observable<FormTypeFilters>;
    public formFilters$: Observable<FormFilters>;
    public selectedParticipant$: Observable<ParticipantCopyId>;
    public displayNACheckbox$: Observable<boolean>;
    public selectedOptionsDisplay$: Observable<string>;
    public displayHistoryButton$: Observable<boolean>;

    public dropDownOptionsFunctionalElementGroup: FormGroup;
    public remarksControl: FormControl;
    public optionSetFormGroup: FormGroup;
    public checkboxControl: FormControl;
    protected readonly FormsConstants = FormsConstants;
    protected readonly FunctionTypes = FunctionTypes;
    public faCheck = faCheck;
    private readonly destroyRef = inject(DestroyRef);

    public isMobile = false;

    public constructor(
        private store: Store<FormsState>,
        private formBuilder: FormBuilder,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly uiResponsiveService: UiResponsiveService,
        private cdr: ChangeDetectorRef,
    ) {
        this.readOnly$ = store.select(getFormParticipantCopyReadonly);
        this.typeFilters$ = store.select(getFormTypeFilters);
        this.formFilters$ = store.select(getFormFilters);
        this.selectedParticipant$ = store.select(getFormSelectedParticipantCopyId);
    }

    public ngOnInit(): void {
        this.remarksControl = this.formBuilder.control('');
        this.checkboxControl = this.formBuilder.control(false);
        this.optionSetFormGroup = this.formBuilder.group({});
        this.dropDownOptionsFunctionalElementGroup = this.formBuilder.group({
            remarksControl: this.remarksControl,
            checkbox: this.checkboxControl,
        });

        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.dropDownOptionsFunctionalElementGroup);
                })
                .catch((error: Error) => {
                    this.log.error(
                        'DropDownOptionsFunctionalElementComponent: ngOnInit: Error in parentForm.push: ',
                        JSON.stringify(error),
                    );
                });
        }

        this.dropDownOptionsFunctionalElement$ = this.store
            .select(getFormFunctionalElementByIdFactory<DropDownOptionsFunctionalElement>({ id: this.id }))
            .pipe(filter(isDefined));

        this.displayHistoryButton$ = this.dropDownOptionsFunctionalElement$.pipe(
            map(
                (dropDownOptionsFunctionalElement) =>
                    isDefined(dropDownOptionsFunctionalElement.uniqueId) &&
                    dropDownOptionsFunctionalElement.uniqueId.length > 0 &&
                    this.configService.getEnableFormsHistoricalValues(),
            ),
        );

        this.readOnly$ = combineLatest([
            this.store.select(getFormParticipantCopyReadonly),
            this.store.select(isCosimaFunctionalElementFromTrainingChecklistReadOnly({ id: this.id })),
        ]).pipe(map(([readOnly, isCosimaReadOnly]) => readOnly || isCosimaReadOnly));

        this.elementTypeLabel$ = combineLatest([
            this.dropDownOptionsFunctionalElement$.pipe(
                map((s) => s.elementType),
                filter(isDefined),
            ),
            this.store.select(getFormTypeFilters),
        ]).pipe(map(([elementType, filters]) => FormUtilities.getFilterLabel(elementType, filters)));

        this.showRemarksField$ = this.dropDownOptionsFunctionalElement$.pipe(
            map((s) => !!(s.remarks?.length || s.choices?.choiceNegative?.selected)),
        );

        this.disableDropDown$ = combineLatest([this.readOnly$, this.dropDownOptionsFunctionalElement$]).pipe(
            map(
                ([readOnly, dropdownFunctionalElement]) =>
                    readOnly ||
                    (isDefined(dropdownFunctionalElement.choices?.choiceNa) &&
                        dropdownFunctionalElement.choices?.choiceNa.selected),
            ),
        );

        this.visible$ = combineLatest([
            this.dropDownOptionsFunctionalElement$,
            this.typeFilters$,
            this.formFilters$,
            this.selectedParticipant$,
        ]).pipe(
            map(([dropDownOptionsFunctionalElement, typeFilters, formFilters, selectedParticipant]) => {
                const choices = dropDownOptionsFunctionalElement.choices;

                if (!isDefined(choices)) {
                    throw new Error(
                        'DropDownOptionsFunctionalElementComponent: ngOnInit: visible$ - no choices available on element',
                    );
                }

                const choicesValues = Object.values(choices);

                const isMasterCopy = selectedParticipant === FormsConstants.MASTER_PARTICIPANT_ID;

                // in participant copies, only content that was answered with "Yes" in this session is displayed
                const selectedInMaster = choicesValues.filter(isDefined).find((c) => c.selectedInMaster);
                const yesInMaster =
                    typeof selectedInMaster !== 'undefined' && selectedInMaster.type === CHOICE_TYPE.CHOICE_POSITIVE;
                const visibleInParticipantCopy = !dropDownOptionsFunctionalElement.contentLoadedFromXml && yesInMaster;

                // filters apply both to master and participant copies
                const visibleByFilter = isCosimaFunctionalElementVisible(
                    dropDownOptionsFunctionalElement,
                    typeFilters,
                    formFilters,
                    dropDownOptionsFunctionalElement.elementType ?? FormsConstants.ELEMENT_TYPE.UNKNOWN,
                );

                const visible = (isMasterCopy || visibleInParticipantCopy) && visibleByFilter;

                return visible;
            }),
        );

        this.selectedOptionsDisplay$ = this.dropDownOptionsFunctionalElement$.pipe(
            map((dropdownOptionsFunctionalElement) => {
                let selectedOptions = '';
                dropdownOptionsFunctionalElement.optionSet.forEach((optionSet) => {
                    const optionsInOptionSet = optionSet.options;
                    selectedOptions += optionsInOptionSet
                        .filter((option) => option.selected)
                        .map((option) => option.label)
                        .join();
                });

                return selectedOptions;
            }),
        );

        this.dropDownOptionsFunctionalElement$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((dropDownOptionsFunctionalElement) => {
                const isChoiceNASelected = dropDownOptionsFunctionalElement.choices?.choiceNa?.selected;
                this.checkboxControl.patchValue(isChoiceNASelected, {
                    emitEvent: false,
                });

                this.remarksControl.patchValue(dropDownOptionsFunctionalElement.remarks, { emitEvent: false });

                if (isChoiceNASelected) {
                    dropDownOptionsFunctionalElement.optionSet.map((groupOptions) => {
                        groupOptions.options.map((option) => {
                            this.store.dispatch(
                                updateOptionSet({
                                    id: this.id,
                                    xmlId: option.id,
                                    newValue: false,
                                }),
                            );
                        });
                    });
                }
            });

        this.displayNACheckbox$ = this.dropDownOptionsFunctionalElement$.pipe(
            map(
                (valueFunctionalElement) =>
                    (!isDefined(valueFunctionalElement.choices) &&
                        valueFunctionalElement.controllingCheckpoint.length === 0) ||
                    isDefined(valueFunctionalElement.choices?.choiceNa),
            ),
        );

        this.remarksControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                withLatestFrom(this.dropDownOptionsFunctionalElement$.pipe(take(1))),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(([newValue, dropDownOptionsFunctionalElement]: [string, DropDownOptionsFunctionalElement]) => {
                if (!isDefined(dropDownOptionsFunctionalElement.xmlId)) {
                    throw new Error(
                        `dropDownOptionsFunctionalElementComponent: remarksControl valueChanges: Functional element has no xml Id!`,
                    );
                }
                if (dropDownOptionsFunctionalElement.remarksXmlId) {
                    this.store.dispatch(
                        updateFunctionalElementRemarks({
                            id: dropDownOptionsFunctionalElement.id,
                            // using ! because remarksXmlId is guaranteed to be there in Cosima. It is only undefined in Astoria.
                            xmlId: dropDownOptionsFunctionalElement.remarksXmlId,
                            newValue,
                        }),
                    );
                }
            });

        this.checkboxControl.valueChanges
            .pipe(
                withLatestFrom(this.dropDownOptionsFunctionalElement$, this.readOnly$),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(([checkboxValue, dropDownOptionsFunctionalElement, readOnly]) => {
                if (readOnly) {
                    return;
                }

                if (dropDownOptionsFunctionalElement.xmlId) {
                    this.store.dispatch(
                        updateValue({
                            xmlId: dropDownOptionsFunctionalElement.xmlId,
                            id: dropDownOptionsFunctionalElement.id,
                            newValue: checkboxValue ? FormsConstants.NA : '',
                            valueXmlId: dropDownOptionsFunctionalElement.remarksXmlId,
                            valid: this.optionSetFormGroup.valid,
                        }),
                    );
                }
            });

        this.uiResponsiveService.isMobile$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isMobile) => {
            this.isMobile = isMobile;
            // need to trigger the changeDetection, otherwise when resizing the screen, the dropdown values will not fit in the
            // dropdown menu.
            this.cdr.detectChanges();
        });
    }

    public ngAfterViewInit(): void {
        // we need to trigger this in the ngAfterViewInit, as if we are doing it in the onInit it will trigger before async pipes get executed,
        // which will result in the visible$ not to be triggered untilt he user is trying to resize the screen.
        // if the visible$ is not being triggered, then the whole component will not display.
        combineLatest([this.visible$, this.uiResponsiveService.isMobile$])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([, isMobile]) => {
                this.isMobile = isMobile;
                // need to trigger the changeDetection, otherwise when resizing the screen, the dropdown values will not fit in the
                // dropdown menu.
                this.cdr.detectChanges();
            });
    }

    public ngOnDestroy(): void {
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex(
                (control) => control === this.dropDownOptionsFunctionalElementGroup,
            );
            this.parentForm.removeAt(index);
        }
    }

    public updateDropDownOptions(option: GroupOption): void {
        this.store.dispatch(
            updateDropDownOptions({
                id: this.id,
                xmlId: option.id,
                newValue: !option.selected,
            }),
        );
    }

    public dropDownButtonText(dropDownOptionsFunctionalElement: DropDownOptionsFunctionalElement): string {
        let dropDownButtonText = '';
        if (dropDownOptionsFunctionalElement.function === FunctionTypes.MULTI_SELECT) {
            const selectedOptionsText = dropDownOptionsFunctionalElement.optionSet.reduce((acc, optionSet) => {
                const selectedOptions = optionSet.options
                    .filter((option) => option.selected)
                    .map((option) => option.label);
                return acc.concat(selectedOptions);
            }, [] as string[]);

            dropDownButtonText = selectedOptionsText.join(',');
        } else if (dropDownOptionsFunctionalElement.function === FunctionTypes.SINGLE_SELECT) {
            dropDownOptionsFunctionalElement.optionSet.forEach((optionSet) => {
                const selectedOptions = optionSet.options.find((option) => option.selected);
                dropDownButtonText = selectedOptions ? selectedOptions.label : '';
            });
        }

        return dropDownButtonText;
    }
}
